import { useEffect, useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { stageMeta } from "../lib/stages";
import AddProspectModal from "../components/AddProspectModal";
import ProspectDrawer from "../components/ProspectDrawer";
import KanbanBoard from "../components/KanbanBoard";

export default function Prospects() {
  const [prospects, setProspects] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/prospects").then(({ data }) => setProspects(data));
  useEffect(() => {
    load();
  }, []);

  const updateLocal = (updated) => {
    setProspects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setSelected((s) => (s && s.id === updated.id ? updated : s));
  };

  const patchProspect = async (id, updates, msg) => {
    const { data } = await api.patch(`/prospects/${id}`, updates);
    updateLocal(data);
    if (msg) toast.success(msg);
    return data;
  };

  const moveStage = (p, stage) => {
    updateLocal({ ...p, stage });
    patchProspect(p.id, { stage }, `Moved to ${stageMeta(stage).label}`).catch(() => {
      updateLocal(p);
      toast.error("Could not move card");
    });
  };

  const filtered = useMemo(() => {
    if (!search) return prospects;
    const q = search.toLowerCase();
    return prospects.filter((p) => [p.name, p.email, p.company, p.phone].filter(Boolean).some((v) => v.toLowerCase().includes(q)));
  }, [prospects, search]);

  return (
    <div className="space-y-6" data-testid="prospects-page">
      <div className="flex flex-wrap items-center justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow mb-1">Lead Intel</p>
          <h1 className="text-3xl font-extrabold tracking-tight text-white">Pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Drag cards between stages to move deals along.</p>
        </div>
        <button
          data-testid="add-prospect-btn"
          onClick={() => setAddOpen(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white font-semibold transition-all duration-300 hover:shadow-[0_0_20px_rgba(70,144,217,0.4)]"
        >
          <Plus size={16} /> Add Prospect
        </button>
      </div>

      <div className="flex items-center gap-3 fade-up">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="prospect-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search prospects…"
            className="pl-9 pr-3 py-2 rounded-full bg-black/30 border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9] w-56"
          />
        </div>
        <span className="text-xs text-slate-500 font-mono-tech">{filtered.length} prospects</span>
      </div>

      <KanbanBoard prospects={filtered} onOpen={setSelected} onMove={moveStage} />

      <AddProspectModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(p) => {
          setProspects((prev) => [p, ...prev]);
          toast.success("Prospect added");
        }}
      />
      <ProspectDrawer
        prospect={selected}
        onClose={() => setSelected(null)}
        onPatch={patchProspect}
        onUpdate={updateLocal}
        onDeleted={(id) => {
          setProspects((prev) => prev.filter((p) => p.id !== id));
          setSelected(null);
          toast.success("Prospect deleted");
        }}
      />
    </div>
  );
}
